import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "getting-library-and-element-data"
    }}>{`Getting Library and Element Data`}</h1>
    <p>{`Any integration with the Libraries API will involve reading information about a user's stored libraries and elements.`}</p>
    <p>{`For example, you may want answers to questions like:`}</p>
    <ul>
      <li parentName="ul">{`What libraries or elements were created in the last week?`}</li>
      <li parentName="ul">{`What is the name of each element in a given library?`}</li>
      <li parentName="ul">{`Where is the thumbnail for a given library element?`}</li>
    </ul>
    <p>{`As you might imagine, you can accomplish all of this and much more with `}<inlineCode parentName="p">{`GET`}</inlineCode>{` requests to the Libraries API.`}</p>
    <p>{`Depending on your use case, you may prefer to make your Libraries API calls from the client or from your server. In this tutorial, we're going to focus on how to make calls from your server with Node.js and Express.`}</p>
    <p>{`In each step, we'll set up an Express route to make an API call and talk a little about the response we get. The accompanying sample repo will embellish slightly on what we cover here by making simple use of the API responses in the browser.`}</p>
    <p>{`Reading through this tutorial will give you a simple jumping off point for learning more about our `}<inlineCode parentName="p">{`GET`}</inlineCode>{` endpoints in the API references.`}</p>
    <h2 {...{
      "id": "before-you-start"
    }}>{`Before you start`}</h2>
    <h3 {...{
      "id": "technology-used"
    }}>{`Technology Used`}</h3>
    <ul>
      <li parentName="ul">{`Node.js`}</li>
      <li parentName="ul">{`npm`}</li>
      <li parentName="ul">{`Express`}</li>
      <li parentName="ul">{`Axios`}</li>
    </ul>
    <h3 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "./how-to-get-your-developer-credentials.md"
        }}>{`How to get your developer credentials`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "./tutorials/how-to-get-access-tokens-with-oauth.md"
        }}>{`How to get access tokens with OAuth`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "./quick-start-nodejs.md"
        }}>{`Quick Start: Node.js`}</a></li>
    </ul>
    <h2 {...{
      "id": "development-steps"
    }}>{`Development Steps`}</h2>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Info`}</strong>{`
Complete code for this tutorial can be found `}<a parentName="p" {...{
          "href": "https://github.com/cc-libraries-api/code-samples/tree/master/getting-data-nodejs"
        }}>{`on GitHub`}</a>{`.`}</p>
    </blockquote>
    <h3 {...{
      "id": "1-bootstrap-an-express-app"
    }}>{`1. Bootstrap an Express app`}</h3>
    <p>{`We'll start by using `}<inlineCode parentName="p">{`express-generator`}</inlineCode>{`, a scaffolding tool provided by Express, a popular server-side framework for Node.js. In your terminal, create your project:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`npx express-generator --ejs --git myapp
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Info`}</strong>{`
You can learn more about `}<inlineCode parentName="p">{`express-generator`}</inlineCode>{` in `}<a parentName="p" {...{
          "href": "https://expressjs.com/en/starter/generator.html"
        }}>{`their docs`}</a>{`. For our purposes here, this is all we need to do before digging in.`}</p>
    </blockquote>
    <p>{`Then navigate to your new project, install dependencies, and start your app:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`cd myapp
npm install --save axios dotenv
touch .env
npm start
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Info`}</strong>{`
For setting up your `}<inlineCode parentName="p">{`.env`}</inlineCode>{` file, see the `}<a parentName="p" {...{
          "href": "https://github.com/cc-libraries-api/api-docs/blob/master/tutorials/quick-start-nodejs.md#1-set-up-your-environment-variables"
        }}>{`"Set up your environment variables" section`}</a>{` in our `}<em parentName="p">{`Quick Start: Node.js`}</em>{` tutorial.`}</p>
    </blockquote>
    <p>{`In your browser, if you navigate to `}<inlineCode parentName="p">{`localhost:3000`}</inlineCode>{`, you'll see the default home page created by `}<inlineCode parentName="p">{`express-generator`}</inlineCode>{`. (We won't be working with UI in this tutorial, but the accompanying sample repo makes very basic use of the API responses in the browser.)`}</p>
    <h3 {...{
      "id": "2-get-metadata-about-your-users-libraries"
    }}>{`2. Get metadata about your user's Libraries`}</h3>
    <p>{`We'll get started with the Libraries API by getting a top-level view of the libraries that the user has stored.`}</p>
    <p><strong parentName="p">{`Creating the route`}</strong></p>
    <p>{`In the app we just generated, we'll create a new route in the `}<inlineCode parentName="p">{`routes/index.js`}</inlineCode>{` file:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`require("dotenv").config();
const express = require("express");
const router = express.Router();
const axios = require("axios"); // Be sure to require axios

const baseURL = "https://cc-libraries.adobe.io/api/v1/libraries";

// The default GET route provided by express-generator
router.get("/", async (req, res, next) => {
  res.render("index", { title: "Creative Cloud Libraries API" });
});

// Our new route
router.get("/cc-libraries/data", async (req, res, next) => {
  const options = {
    headers: {
      "x-api-key": process.env.API_KEY,
      Authorization: \`Bearer \${process.env.ACCESS_TOKEN}\`,
    },
  };

  try {
    const response = await axios.get(baseURL, options);
    res.json(response.data);
  } catch (error) {
    console.log(error);
    next(error);
  }
});

module.exports = router;
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Info`}</strong>{`
Above this route, we've also set the `}<inlineCode parentName="p">{`baseURL`}</inlineCode>{` variable. We'll use this variable in other routes later on, so make sure you've set it outside of the route as you see above.`}</p>
    </blockquote>
    <p><strong parentName="p">{`What it does`}</strong></p>
    <p>{`The heart of the `}<inlineCode parentName="p">{`"/cc-libraries/data"`}</inlineCode>{` route is where we make the API call (for this example, we're using the `}<inlineCode parentName="p">{`axios`}</inlineCode>{` module for making HTTP requests):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const response = await axios.get(baseURL, options);
res.json(response.data);
`}</code></pre>
    <p>{`If everything goes well, we'll get a response from the Libraries API that contains JSON data (in this example, within `}<inlineCode parentName="p">{`response.data`}</inlineCode>{`) that we return to the browser.`}</p>
    <p><strong parentName="p">{`Try it out`}</strong></p>
    <p>{`You can access this endpoint by navigating your browser to:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`http://localhost:3000/cc-libraries/data
`}</code></pre>
    <p>{`The response will look something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "total_count": 2,
  "libraries": [
    /* Your libraries here */
  ]
}
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`libraries`}</inlineCode>{` array contains a JSON object full of metadata related to each of your user's Libraries. Each object in this array will have a unique `}<inlineCode parentName="p">{`id`}</inlineCode>{` property for that specific library:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "id": "AAAAAA-AAAA-AAAA-AAAA-AAAAAAAAAAAA",
  "name": "My Libraray",
  "created_date": 1565044330560,
  "modified_date": 1568329791348
  /* More library metadata */
}
`}</code></pre>
    <p>{`Go ahead and copy an `}<inlineCode parentName="p">{`id`}</inlineCode>{` from one of your libraries. We'll use it in the next step.`}</p>
    <h3 {...{
      "id": "3-get-metadata-for-elements-in-a-specific-library"
    }}>{`3. Get metadata for elements in a specific Library`}</h3>
    <p>{`Next we'll get a list of elements contained in a given library.`}</p>
    <p><strong parentName="p">{`Creating the route`}</strong></p>
    <p>{`Back in `}<inlineCode parentName="p">{`routes/index.js`}</inlineCode>{`, we'll create a route for retreiving metadata for a specific Library's elements:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`router.get("/cc-libraries/data/:libraryId", async (req, res, next) => {
  const { libraryId } = req.params;

  const options = {
    headers: {
      "x-api-key": process.env.API_KEY,
      Authorization: \`Bearer \${process.env.ACCESS_TOKEN}\`,
    },
  };

  try {
    const response = await axios.get(
      \`\${baseURL}/\${libraryId}/elements\`,
      options
    );
    res.json(response.data);
  } catch (error) {
    console.log(error);
    next(error);
  }
});
`}</code></pre>
    <p><strong parentName="p">{`What it does`}</strong></p>
    <p>{`Our route above takes a `}<inlineCode parentName="p">{`libraryId`}</inlineCode>{` parameter. This parameter is then used to create the Libraries API endpoint we want to call:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`\`\${baseURL}/\${libraryId}/elements\`;
// Or, https://cc-libraries.adobe.io/api/v1/libraries/AAAAAA-AAAA-AAAA-AAAA-AAAAAAAAAAAA/elements
`}</code></pre>
    <p>{`As in the first route, we use the `}<inlineCode parentName="p">{`axios`}</inlineCode>{` module to make the HTTP request. If the request is successful, we get a JSON object from the Libraries API that we send back to the browser.`}</p>
    <p><strong parentName="p">{`Try it out`}</strong></p>
    <p>{`You can access this endpoint by navigating your browser to this URL (be sure to swap in the library ID you copied in the previous step):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`http://localhost:3000/cc-libraries/data/AAAAAA-AAAA-AAAA-AAAA-AAAAAAAAAAAA
`}</code></pre>
    <p>{`The response will look something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "total_count": 60,
  "elements": [
    /* Library elements here */
  ]
}
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`elements`}</inlineCode>{` array contains a JSON object full of metadata related to each of the elements stored in a given Library:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "id": "BBBBBBBB-BBBB-BBBB-BBBB-BBBBBBBBB",
  "name": "An Element Name",
  "created_date": 1565044330560,
  "modified_date": 1568329791348,
  "type": "application/vnd.adobe.element.pattern+dcx",
  "thumbnail": {},
  "groups": [],
  "assetSubType": "element"
}
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Info`}</strong>{`
It's worth noting that if you already have a specific element ID that you want metadata for, you can request it directly using the element ID (e.g., `}<inlineCode parentName="p">{`\${baseURL}/\${libraryId}/elements/\${elementId}`}</inlineCode>{`). See the endpoint references for more details.`}</p>
    </blockquote>
    <h3 {...{
      "id": "4-getting-image-renditions-and-thumbnails"
    }}>{`4. Getting image renditions and thumbnails`}</h3>
    <p>{`Next we'll get an image rendition for a specific element.`}</p>
    <p><strong parentName="p">{`Creating the route`}</strong></p>
    <p>{`Back in `}<inlineCode parentName="p">{`routes/index.js`}</inlineCode>{`, we'll create a route for retreiving an image representation of a Library element:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`router.get("/cc-libraries/image", async (req, res, next) => {
  let { url } = req.query;
  url = url.slice(0, url.lastIndexOf("/")); // See the Info box below

  const options = {
    responseType: "arraybuffer",
    headers: {
      "x-api-key": process.env.API_KEY,
      Authorization: \`Bearer \${process.env.ACCESS_TOKEN}\`,
    },
  };

  try {
    const response = await axios.get(url, options);
    const dataUrl = getBase64dataUrl(response);

    res.set("Content-Type", response.headers["content-type"]);
    res.set("Content-Length", response.headers["content-length"]);
    res.send(dataUrl);
  } catch (error) {
    console.log(error.message);
    next(error);
  }
});

const getBase64dataUrl = (response) => {
  const base64flag = \`data:\${response.headers["content-type"]};base64,\`;
  const base64string = Buffer.from(response.data, "binary").toString("base64");

  return \`\${base64flag}\${base64string}\`;
};
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Info`}</strong>{`
Note that above, for simplicity we are naively slicing off from the `}<inlineCode parentName="p">{`url`}</inlineCode>{` query the rendition argument the Libraries API provides by default, which is `}<inlineCode parentName="p">{`:rendition;size=200`}</inlineCode>{`. Slicing this off gives you a full-size image, which in many cases may not be ideal due to size. If you have a specific size in mind, you can change `}<inlineCode parentName="p">{`200`}</inlineCode>{` to another value, or keep the query in tact.`}</p>
    </blockquote>
    <p><strong parentName="p">{`What it does`}</strong></p>
    <p>{`Our route above takes a `}<inlineCode parentName="p">{`url`}</inlineCode>{` query (`}<inlineCode parentName="p">{`/cc-libraries/image?url=https://url-here`}</inlineCode>{`). The value of this query (which is the element's thumbnail URL we copied in the previous step) is then used to call the Adobe storage service:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const response = await axios.get(url, options);
`}</code></pre>
    <p>{`Again, we use the `}<inlineCode parentName="p">{`axios`}</inlineCode>{` module to make the HTTP request. If the request is successful, we receive image data that we can do further work with.`}</p>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Info`}</strong>{`
Note that, unlike in our previous routes, here we are not using the Libraries API `}<inlineCode parentName="p">{`baseURL`}</inlineCode>{` variable that we defined earlier. Images come from another service which we will call directly.`}</p>
    </blockquote>
    <p>{`The route will then transform the response data from an array buffer to a base64 data URL (`}<inlineCode parentName="p">{`dataUrl`}</inlineCode>{`) that can be used as the `}<inlineCode parentName="p">{`src`}</inlineCode>{` of an image element in the browser. This step is just for the purpose of demonstration; you might have other uses for the image data.`}</p>
    <p><strong parentName="p">{`Try it out`}</strong></p>
    <p>{`You can access this endpoint by navigating your browser to this URL (be sure to swap in the thumbnail URL you copied in the previous step):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`http://localhost:3000/cc-libraries/image?url=https://url-here
`}</code></pre>
    <p>{`Your browser will receive a base64 string representing the image data. Since we haven't set up an image element to display the image data, you'll need to have a look in your browser's developer tools to see the data. In Chome DevTools for example, you can find the data string in the `}<em parentName="p">{`Sources`}</em>{` tab, under `}<em parentName="p">{`top > localhost:{port} > cc-libraries > image?url=https://url-here`}</em>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-base64"
      }}>{`data:image/jpeg;base64,base64stringhere...
`}</code></pre>
    <p>{`If we wanted to display the image, this base64 data URL (`}<inlineCode parentName="p">{`dataUrl`}</inlineCode>{`) could be used as the `}<inlineCode parentName="p">{`src`}</inlineCode>{` of an image element. In fact, the accompanying sample repo does this if it's something you're interested in seeing.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      